import * as React from 'react';
import { CurrencyContext, LocaleContext, consumer } from '@tock/coretypes';
import { Grid } from 'ui';
import { HeroCard, Skeleton, computeHeroDesktopHeight, computeHeroMobileHeight } from 'omakase';
import { IntlProvider } from 'react-intl';
import { VariableSizeList as List } from 'react-window';
import { MobileNativeContext } from 'providers/withApplicationContext';
import { QueryStringParams } from 'types';
import {
  SEARCH_SCROLL_KEY,
  SearchScrollValue,
  sessionPrioritizedBackend,
} from 'utils/storage-backends/backends';
import { SearchQueryStringParams } from 'components/search/utils';
import { Theme, css, useTheme } from '@emotion/react';
import { getAutoFormatUrl } from '@tock/images';
import { getAvailability } from 'selectors/availabilitySearchSelectors';
import { getResultItemSize } from 'utils/offerings';
import { lockSelector } from 'selectors/lockSelectors';
import { parse } from '@tock/query-string';
import { useDidUpdateEffect, usePrevious } from '@tock/hooks';
import { useExperienceCardAction } from 'hooks/search/useExperienceCardAction';
import { useFlatSearchQuery } from 'hooks/search/useFlatSearchQuery';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@material-ui/core';
import { useQueryStringParams } from 'hooks/useQueryStringParams';
import { useRoutedCollectionPageInfo } from 'selectors/consumerPageSelectors';
import { useSearchHandlerContext } from 'components/search/SearchContext';
import { useSearchResults } from 'hooks/search/useSearchResults';
import { useSelector } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import AvailabilitySearchResultSimple from 'components/www/availability-search/AvailabilitySearchResultSimple';
import EmptyStateCitySearch from 'components/www/availability-search/EmptyState';
import _ from 'lodash';
import clsx from 'clsx';

/**
 *
 * this is a bit fragile, as our current flow we switch the size in the url for experience modal to work properly
 * and that is coming from the experience. however the scroll position has a different size potentially when that happens when the
 * search is executed.
 *
 */
const getIsSameSearch = (prevSearch: QueryStringParams, search: QueryStringParams): boolean => {
  const isSameMinusSize =
    prevSearch.city === search.city &&
    prevSearch.date === search.date &&
    prevSearch.q === search.q &&
    prevSearch.time === search.time &&
    prevSearch.type === search.type &&
    prevSearch.dateEnd === search.dateEnd &&
    prevSearch.displayTerm === search.displayTerm;
  // when does this happen? when you click on order now on a sizeless single unit quantity thing. it switches the size and navigates
  // when the routes switch like this, maybe we need to update the key?  this works in most cases.
  if (Number(prevSearch.size) === 1 && Number(search.size) > 1) {
    return isSameMinusSize;
  } else {
    return isSameMinusSize && prevSearch.size === search.size;
  }
};

const gridContainer = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: ${theme.spacing(3)}px;
  margin-right: ${theme.spacing(1.5)}px;
`;

export const AvailabilitySearchResultsSimple: React.FunctionComponent = () => {
  const { requestInProgress, result } = useSearchResults();
  const lock = useSelector(lockSelector);
  const availability = useSelector(getAvailability);
  const theme = useTheme();
  const queryStringParams = useQueryStringParams<SearchQueryStringParams>();
  const flatSearchQuery = useFlatSearchQuery();
  const matchesMd = useMediaQuery(theme.breakpoints.up('md'));
  const listRef = React.createRef<List>();
  const location = useLocation();
  const {
    lockTimeAndSize,
    navigateToExperience,
    navigateToExperienceMenu,
    selectedBusinessId,
    setSelectedBusinessId,
    mode,
    header,
  } = { ...useSearchHandlerContext() };
  const experienceCardAction = useExperienceCardAction(mode);

  // Collection Page-related hooks
  const {
    name: collectionPageHeader,
    decorativeHeroImageUrl,
    decorativeHeroImageUrlDesktop,
    decorativeHeroImageAltText,
    decorativeHexColor,
  } = useRoutedCollectionPageInfo();

  const effectiveSearchHeader = collectionPageHeader ?? header;

  // We're using React.useMemo() here to ensure that nameAndColorProps, which is a dependency of the
  // scrollToBusinessId useCallback(), won't change on every render
  const nameAndColorProps = React.useMemo(() => {
    return effectiveSearchHeader === undefined
      ? undefined
      : {
          primaryText: effectiveSearchHeader,
          color: decorativeHexColor,
        };
  }, [decorativeHexColor, effectiveSearchHeader]);

  const heroCardHeadingElement = 'h1' as React.ElementType;

  const hasInitializedList = React.useRef<boolean>(false);

  const hideExperiences = mode?.stateType === 'togo' ? !queryStringParams.date : false;

  const storeScrollState = (scrollState: SearchScrollValue) => {
    sessionPrioritizedBackend.set(SEARCH_SCROLL_KEY, JSON.stringify(scrollState));
  };

  const clearScrollState = () => {
    sessionPrioritizedBackend.remove(SEARCH_SCROLL_KEY);
  };

  const scrollToBusinessId = React.useCallback(
    (businessId: number) => {
      if (listRef.current) {
        const businessIndex = result?.offeringAvailability
          ? result?.offeringAvailability.findIndex((offeringResult) => {
              return offeringResult?.business?.id === businessId;
            })
          : undefined;
        if (!_.isNil(businessIndex) && businessIndex !== -1) {
          // For Collection pages that render a HeroCard (indicated by the presence of nameAndColorProps), the
          // HeroCard will be rendered at the 0th index, so we must increase the scroll-to index target by 1
          // ---------
          // we don't bother storing a business id scroll state here because the onScroll behavior below will just overwrite it with an offset based value
          listRef.current.scrollToItem(businessIndex + (nameAndColorProps ? 1 : 0), 'start');

          // in mobile, we may go to map view, change the selected pin, go to the business page, go back, then click the map view again and expect the selected pin to be restored
          if (!selectedBusinessId) {
            setSelectedBusinessId && setSelectedBusinessId(businessId);
          }
        }
      }
    },
    [
      listRef,
      nameAndColorProps,
      result?.offeringAvailability,
      selectedBusinessId,
      setSelectedBusinessId,
    ]
  );

  const scrollToEntry = React.useCallback(
    (scrollTopEntry: SearchScrollValue) => {
      switch (scrollTopEntry.type) {
        case 'offset':
          listRef?.current?.scrollTo(scrollTopEntry.scrollOffset);
          break;
        case 'businessId':
          scrollToBusinessId(scrollTopEntry.selectedBusinessId);
          break;
        default:
          // old scroll state style stored, clear it
          clearScrollState();
      }
    },
    [listRef, scrollToBusinessId]
  );

  const scrollToTop = React.useCallback(() => {
    listRef?.current?.scrollTo(0);
    storeScrollState({
      type: 'offset',
      scrollOffset: 0,
      key: `${location.pathname}:${location.search}`,
    });
  }, [listRef, location.pathname, location.search]);

  // only after "mount" due to SSR differences
  React.useEffect(() => {
    if (listRef.current && !hasInitializedList.current) {
      listRef.current.resetAfterIndex(0);
      const storedScrollTopEntry = sessionPrioritizedBackend.get(SEARCH_SCROLL_KEY);
      if (!storedScrollTopEntry) {
        hasInitializedList.current = true;
        return;
      }
      const scrollTopEntry: SearchScrollValue = JSON.parse(storedScrollTopEntry);
      const { key } = scrollTopEntry;
      const [pathname, search] = key.split(':');
      const parsedSearchKey = parse<QueryStringParams>(search);
      const parsedLocationSearch = parse<QueryStringParams>(location.search);

      const isSameSearch = getIsSameSearch(parsedSearchKey, parsedLocationSearch);

      if (pathname === location.pathname && !isSameSearch) {
        scrollToTop();
      } else if (pathname === location.pathname && isSameSearch) {
        scrollToEntry(scrollTopEntry);
      }

      hasInitializedList.current = true;
    }
  }, [listRef, location.pathname, location.search, scrollToEntry, scrollToTop]);

  const prevResults = usePrevious(result);

  // the results changed after initial setup
  // reset the react-window cache to force re-render
  // scroll to top
  useDidUpdateEffect(() => {
    if (listRef.current && prevResults && !_.isEqual(prevResults, result)) {
      listRef.current.resetAfterIndex(0);
      listRef.current.scrollToItem(0);
    }
  }, [listRef, result, prevResults]);

  // this will scroll the list of results to the selected business id, which
  // mostly changes from clicking on the map markers
  React.useEffect(() => {
    if (!selectedBusinessId) {
      return;
    }
    scrollToBusinessId(selectedBusinessId);
  }, [listRef, result?.offeringAvailability, scrollToBusinessId, selectedBusinessId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveScroll = React.useCallback(
    _.debounce((scrollOffset: number) => {
      storeScrollState({
        type: 'offset',
        scrollOffset,
        key: `${location.pathname}:${location.search}`,
      });
    }, 250),
    []
  );

  // have to measure differently depending on if mobile or not
  const getItemSize = (index: number) => {
    if (!result) {
      return 0;
    }
    if (effectiveSearchHeader) {
      if (index === 0) {
        const computeHeightArg = {
          color: decorativeHexColor,
          srcMobile: decorativeHeroImageUrl,
          secondaryText: 'X results',
        };
        return matchesMd
          ? computeHeroDesktopHeight(theme, computeHeightArg)
          : computeHeroMobileHeight(theme, computeHeightArg);
      } else {
        --index;
      }
    }
    return getResultItemSize({ index, results: result, hideExperiences, matchesMd });
  };
  const getItemKey = (index: number) => {
    const resultItem = result?.offeringAvailability
      ? result.offeringAvailability[index]
      : undefined;
    return resultItem?.business?.id ?? index;
  };

  const imageProps =
    decorativeHeroImageUrl && decorativeHeroImageUrlDesktop && decorativeHeroImageAltText
      ? {
          srcMobile: getAutoFormatUrl(decorativeHeroImageUrl),
          srcDesktop: getAutoFormatUrl(decorativeHeroImageUrlDesktop),
          alt: decorativeHeroImageAltText,
        }
      : {};

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    if (nameAndColorProps) {
      if (index === 0) {
        const offeringAvailabilityCount = result?.offeringAvailability.length ?? 0;

        const nonImageProps = {
          ...nameAndColorProps,
          headingComponent: heroCardHeadingElement,
          secondaryText:
            offeringAvailabilityCount === 1 ? '1 result' : `${offeringAvailabilityCount} results`,
        };

        return (
          <li style={style} key="heroCard" suppressHydrationWarning>
            <HeroCard {...imageProps} {...nonImageProps} />
          </li>
        );
      } else {
        --index;
      }
    }

    const resultItem = result?.offeringAvailability
      ? result.offeringAvailability[index]
      : undefined;
    const business = resultItem?.business as consumer.OfferingBusiness;
    const destKey = `DestResult-${business?.id}`;
    const distanceMeters =
      typeof resultItem?.ranking?.distanceMeters === 'number'
        ? resultItem?.ranking.distanceMeters
        : undefined;

    return (
      <li
        style={style}
        key={business.id}
        id={`availability-list-business-${business.id}`}
        className={clsx('AvailabilitySearch-destinationResultList-item', destKey)}
        data-businessid={business.id}
        data-testid={`businessId-${business.id}`}
        suppressHydrationWarning
      >
        <IntlProvider locale={business.locale}>
          <LocaleContext.Provider value={business.locale}>
            <CurrencyContext.Provider value={business.currencyCode}>
              {lockTimeAndSize && navigateToExperience && navigateToExperienceMenu && (
                <AvailabilitySearchResultSimple
                  business={business}
                  distanceMeters={distanceMeters}
                  experienceCardAction={experienceCardAction}
                  hideExperiences={hideExperiences}
                  locale={business.locale}
                  lock={lock}
                  onLockTimeAndSize={lockTimeAndSize}
                  onNavigateToExperience={navigateToExperience}
                  onNavigateToExperienceMenu={navigateToExperienceMenu}
                  query={flatSearchQuery}
                  searchOfferings={resultItem?.offering as consumer.SearchOffering[]}
                  timeZone={business.timeZone}
                  index={index}
                />
              )}
            </CurrencyContext.Provider>
          </LocaleContext.Provider>
        </IntlProvider>
      </li>
    );
  };

  if (requestInProgress) {
    return (
      <div css={gridContainer}>
        <Grid container spacing={7}>
          {_.times(5, (index) => {
            return <AvailabilitySearchResultSimpleSkeleton key={index} />;
          })}
        </Grid>
      </div>
    );
  }

  const innerElementType = React.forwardRef(
    ({ style, ...rest }: { style: React.CSSProperties }, ref: React.Ref<HTMLUListElement>) => {
      let computedHeight = parseFloat(style.height as string);
      if (React.useContext(MobileNativeContext).mobileNative) {
        computedHeight += 36;
        // An appropriate additional height for the mobile native case. For other
        // cases we just use the received height.
      }
      return (
        <ul
          ref={ref}
          style={{
            ...style,
            height: `${computedHeight}px`,
          }}
          suppressHydrationWarning
          {...rest}
        />
      );
    }
  );

  if (result?.count ?? 0 > 0) {
    return (
      <AutoSizer defaultHeight={800} defaultWidth={400}>
        {({ height, width }) => (
          <List
            ref={listRef}
            itemKey={getItemKey}
            className="AvailabilitySearch-destinationResultList AvailabilitySearch-destinationResultList--simple"
            innerElementType={innerElementType}
            height={height}
            itemCount={(result?.offeringAvailability.length ?? 0) + (effectiveSearchHeader ? 1 : 0)}
            width={width}
            itemSize={getItemSize}
            onScroll={({ scrollOffset }) => saveScroll(scrollOffset)}
          >
            {Row}
          </List>
        )}
      </AutoSizer>
    );
  }
  if (!requestInProgress && (!result || result.count === 0) && availability.isInitialized) {
    const nonImageProps = nameAndColorProps && {
      ...nameAndColorProps,
      secondaryText: '0 results',
      headingComponent: heroCardHeadingElement,
    };

    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
        `}
      >
        {nonImageProps ? (
          <div>
            <HeroCard {...imageProps} {...nonImageProps} />
          </div>
        ) : null}
        <EmptyStateCitySearch />
      </div>
    );
  }

  return null;
};

// Skeleton-specific css`` definitions:
const mobileBusinessPic = ({ breakpoints }: Theme) => css`
  ${breakpoints.up('sm')} {
    display: none;
  }
`;

const desktopBusinessPic = ({ breakpoints }: Theme) => css`
  ${breakpoints.down('sm')} {
    display: none;
  }
`;

const titleBlock = ({ breakpoints, spacing }: Theme) => css`
  ${breakpoints.up('sm')} {
    margin-top: ${spacing(3)}px;
  }
`;

const AvailabilitySearchResultSimpleSkeleton: React.FunctionComponent = () => {
  return (
    <Grid item container spacing={2} xs={12}>
      <Grid item xs={8}>
        <Skeleton css={titleBlock} variant="text" height={48} width={'80%'} />
        <Skeleton variant="text" height={24} width={'60%'} />
      </Grid>
      <Grid item xs={4}>
        <Skeleton css={mobileBusinessPic} variant="rect" height={75} rounded />
        <Skeleton css={desktopBusinessPic} variant="rect" height={120} rounded />
      </Grid>
      <Grid item xs={12}>
        <Skeleton variant="rect" height={150} />
      </Grid>
    </Grid>
  );
};
